import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API, Pagination } from '../../services/api/api';
import { DateTime } from 'luxon';
import { SbNavigationPathParam } from '@ngx-seatback/utils';
import { filter, finalize, switchMap, take, takeWhile } from 'rxjs/operators';
import { BehaviorSubject, timer } from 'rxjs';
import { ErrorService } from '../../services/error.service';
import { ModalService } from '../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { AiAPI } from '../../services/api/aiApi';
import { PermissionService } from '../../services/permission.service';
import { AiAPIService } from '../../services/api/aiApi.service';
import { ClientSettingService } from '../../services/client-setting.service';

type ChatDTO = { message: string; timestamp: number; id?: number };
export type NavigationDTO = { routingId: string; pathParameter: SbNavigationPathParam[]; queryParameter: any };
type ChatFullDTO = ChatDTO & { response?: string; navigationDTO?: NavigationDTO[] };

type Result<T> = {
  status: number | null;
  error: string | null;
  errorCode: number | null;
  success: boolean | null;
  message: string | null;
  data: T | null;
  pagination: Pagination | null;
  _type: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class KiService {
  private readonly httpClient = inject(HttpClient);
  private readonly clientSettingService = inject(ClientSettingService);
  private readonly aiAPI = inject(AiAPI);
  private readonly aiAPIService = inject(AiAPIService);
  private readonly api = inject(API);
  private readonly errorService = inject(ErrorService);
  private readonly modalService = inject(ModalService);
  private readonly permissionService = inject(PermissionService);
  private readonly translate = inject(TranslateService);

  private _history = signal<ChatFullDTO[]>([]);
  history = this._history.asReadonly();
  private defaultPagination: Pagination = { resultcount: -1, count: 10, page: 1, pages: -1 };
  private pagination: Pagination = { ...this.defaultPagination };
  loadingHistory = signal(true);
  loadingResponse = signal(true);
  scrollBottom$ = new BehaviorSubject<boolean>(false);
  isServiceConnected = signal(false);

  showKIPanel: boolean = false;
  animationCompleted: boolean = false;

  constructor() {
    this.initializeKIConnection();
  }

  initializeKIConnection() {
    this.isServiceConnected.set(false);

    if (this.clientSettingService.clientSettings()?.['ai_access']) {
      return;
    }

    if (!this.permissionService.hasPermission(this.permissionService.Permission.ai_access)) {
      return;
    }

    this.retrieveLoginInformation();
  }

  retrieveLoginInformation() {
    this.api
      .shared_System_aiServiceURL()
      .pipe(
        take(1),
        switchMap(url => {
          this.aiAPIService._url = url;
          return this.api.auth_Authentication_createAILoginToken();
        }),
        take(1),
        switchMap(loginToken => this.aiAPI.Authenticate_login(loginToken)),
        take(1)
      )
      .subscribe({
        next: result => {
          if (result) {
            this.isServiceConnected.set(true);
          }
        },
        error: error => {
          if (error.status === 423) {
            return;
          }
          this.errorService.errorHandler(error);
        },
      });
  }

  init() {
    if (!this.isServiceConnected()) {
      throw new Error('Service not connected!');
    }

    this.loadingHistory.set(true);
    this._history.set([]);
    // loading history

    // checking server status (ki in progress)
    this.loadingResponse.set(true);
    timer(0, 15000) // polling every 15s
      .pipe(
        switchMap(() => this.httpClient.get<Result<{ status: boolean }>>(this.aiAPIService._url + '/Chat/Status?', {})),
        takeWhile(result => result.data.status === true, true), // while in progress repeat
        filter(result => result.data.status === false),
        take(1),
        switchMap(() => {
          const url =
            this.aiAPIService._url +
            '/Chat/History?' +
            (this.pagination ? 'page=' + this.pagination.page + '&count=' + this.pagination.count : '');
          return this.httpClient.get<Result<ChatFullDTO[]>>(url, {});
        }),
        take(1),
        finalize(() => {
          this.loadingResponse.set(false);
          this.loadingHistory.set(false);
        })
      )
      .subscribe({
        next: response => {
          const chats = response.data;
          this.pagination = response.pagination;

          this._history.set([...chats, ...[]]);
          this.scrollBottom$.next(true);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
  }

  sendMessage(pMessage: string) {
    const chat: ChatDTO = { message: pMessage, timestamp: DateTime.local().toMillis() };
    this._history.update(history => [...history, chat]);
    this.scrollBottom$.next(true);
    this.loadingResponse.set(true);
    this.httpClient
      .post<Result<ChatFullDTO>>(
        this.aiAPIService._url + '/Chat/Navigation',
        {
          createdAt: chat.timestamp,
          message: chat.message,
        },
        {}
      )
      .pipe(
        take(1),
        finalize(() => this.loadingResponse.set(false))
      )
      .subscribe({
        next: response => {
          this._history.update(history => [...history, response.data]);
          this.scrollBottom$.next(true);
          if (this.pagination) {
            this.pagination.resultcount += 2;
          }
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
  }

  loadOlder() {
    if (this.pagination.page >= this.pagination.pages) {
      return;
    }
    this.pagination.page++;
    this.refreshFromServer();
  }

  private refreshFromServer() {
    this.loadingHistory.set(true);
    const url =
      this.aiAPIService._url +
      '/Chat/History?' +
      (this.pagination ? '&page=' + this.pagination.page + '&count=' + this.pagination.count : '');
    this.httpClient
      .get<Result<ChatFullDTO[]>>(url, {})
      .pipe(
        take(1),
        finalize(() => {
          this.loadingHistory.set(false);
        })
      )
      .subscribe({
        next: response => {
          const chats = response.data;
          this.pagination = response.pagination;
          this._history.update(history => [...chats, ...history]);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
  }

  toggleKIPanel() {
    if (this.showKIPanel) {
      this.closeKIPanel();
    } else {
      this.openKIPanel();
    }
  }

  openKIPanel() {
    this.showKIPanel = true;
  }

  closeKIPanel() {
    this.showKIPanel = false;
    this.resetPagination();
  }

  private resetPagination() {
    this.pagination = { ...this.defaultPagination };
  }

  clearHistory() {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.clearHistory'),
        message: this.translate.instant('lang.globals.clearHistoryMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.loadingHistory.set(true);
          this.httpClient
            .delete<Result<ChatFullDTO>>(this.aiAPIService._url + '/Chat/History')
            .pipe(
              take(1),
              finalize(() => {
                this.loadingHistory.set(false);
              })
            )
            .subscribe({
              next: () => {
                this._history.set([]);
              },
              error: err => {
                this.errorService.errorHandler(err);
              },
            });
        }
      })
      .catch(() => {});
  }
}
